import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useStore from "hooks/useStore";
import useTeamMembers from "containers/team-management/useHooks";

interface Props {
  teamMemberId: number;
}

const daysOfWeek = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const OperatingHours = ({ teamMemberId }: Props) => {
  const [data, setData] = useState<any[]>([]);
  const { addAvailableSlots, getAvailableSlotsByID } = useTeamMembers();
  const navigate = useNavigate();
  const { getUser } = useStore();
  const userData = getUser();

  const [hours, setHours] = useState(
    daysOfWeek.map((day) => ({
      day,
      startTime: "",
      endTime: "",
      status: false,
    }))
  );

  const handleTimeChange = (
    index: number,
    field: "startTime" | "endTime",
    value: string
  ) => {
    const updated = [...hours];
    updated[index][field] = value;

    if (updated[index].startTime && updated[index].endTime) {
      updated[index].status = true;
    }

    setHours(updated);
  };

  const toggleDay = (index: number) => {
    const updated = [...hours];
    updated[index].status = !updated[index].status;
    setHours(updated);
  };

  const onSave = async () => {
    const enabledSlots = hours
      .filter((item) => item.status)
      .map((slot) => ({
        day: slot.day,
        startTime: slot.startTime,
        endTime: slot.endTime,
        status: slot.status,
      }));

    const payload = {
      slots: enabledSlots,
    };

    await addAvailableSlots(payload, {
      teamMemberId,
    });
  };

  useEffect(() => {
    const fetchAvailableSlots = async () => {
      await getAvailableSlotsByID(
        (responseData: any[]) => {
          setData(responseData);

          const updatedHours = daysOfWeek.map((day) => {
            const slotForDay = responseData.find((slot) => slot.day === day);
            return {
              day,
              startTime: slotForDay?.startTime || "",
              endTime: slotForDay?.endTime || "",
              status: !!slotForDay,
            };
          });

          setHours(updatedHours);
        },
        { userId: teamMemberId }
      );
    };

    fetchAvailableSlots();
  }, [teamMemberId]);

  return (
    <div>
      <h2 className="text-sm font-semibold mb-4">Operating Hours</h2>

      <div className="space-y-4">
        {hours.map((item, index) => {
          const canEnable = item.startTime && item.endTime;

          return (
            <div
              key={item.day}
              className="flex items-center gap-4 flex-wrap sm:flex-nowrap"
            >
              <div className="w-[100px] font-bold text-sm">{item.day}</div>

              <input
                type="time"
                value={item.startTime}
                onChange={(e) =>
                  handleTimeChange(index, "startTime", e.target.value)
                }
                className="px-3 py-3 border border-lightGrayBorder text-primaryText outline-none rounded-[8px] text-sm bg-white w-[140px]"
              />

              <input
                type="time"
                value={item.endTime}
                onChange={(e) =>
                  handleTimeChange(index, "endTime", e.target.value)
                }
                className="px-3 py-3 border border-lightGrayBorder text-primaryText outline-none rounded-[8px] text-sm bg-white w-[140px]"
              />

              <label className="switch-field">
                <input
                  type="checkbox"
                  checked={item.status}
                  onChange={() => toggleDay(index)}
                  disabled={!canEnable}
                />
                <span className="slider"></span>
              </label>
            </div>
          );
        })}
      </div>

      <div className="flex gap-3 mt-8">
        <button
          className="bg-darkTealBlue text-xs font-medium text-white px-5 py-2 rounded-md"
          onClick={onSave}
        >
          Save
        </button>
        <button className="bg-neutralBg text-xs font-medium text-warmGray px-5 py-2 rounded-md">
          Cancel
        </button>
      </div>
    </div>
  );
};

export default OperatingHours;
